import ProductState from 'classes/productstate';
import ajax from 'controllers/ajax';
import type * as DB from 'interfaces/database';
import moment from 'moment';
import {
	CartItemsModule,
	ProductsModule,
	ProductStateModule,
} from 'store';
import reportError from 'utils/report-error';

type ProductDeleteInput = number | Pick<DB.ProductModel, 'id' | 'read_token'>;

function getProductId(product: ProductDeleteInput): number {
	return typeof product === 'number'
		? product
		: product.id;
}

function getProductModel(product: ProductDeleteInput): Pick<DB.ProductModel, 'id' | 'read_token'> | undefined {
	if (typeof product !== 'number') {
		return product;
	}

	const activeProductModel = ProductStateModule.getProduct;

	return ProductsModule.getById(product) || (
		activeProductModel?.id === product
			? activeProductModel
			: undefined
	);
}

async function softDeleteProduct(productId: number): Promise<void> {
	await ajax.request<void>(
		{
			method: 'put',
			url: `/api/product/${productId}`,
			headers: {
				'content-type': 'application/json; charset=utf-8',
			},
			data: {
				id: productId,
				deleted: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
			},
		},
		{
			auth: true,
			retry: 1,
			debug: {
				offline: true,
				dialog: true,
				abort: true,
			},
		},
	);
}

/**
 * Deletes a product and clears local product-state storage when product data is available.
 *
 * @param product - Product id or product model being deleted.
 * @returns Promise that resolves after cart cleanup, local cleanup, and server deletion.
 */
export default function deleteProduct(product: ProductDeleteInput): Promise<void> {
	const productId = getProductId(product);
	const productModel = getProductModel(product);

	// remove product from shopping cart
	const toRemove = CartItemsModule.where({
		productid: productId,
	});

	// Remove cartitem with this product
	const promises = toRemove.map(
		(cartItemModel) => CartItemsModule.destroyModel({
			id: cartItemModel.id,
		}),
	);
	return Promise.all(promises)
		.then(() => {
			// reset product under construction
			if (ProductStateModule.productId == productId) {
				return ProductState.reset();
			}

			return Promise.resolve();
		})
		.then(() => {
			if (productModel) {
				// Remove from local storage (if exists)
				return ProductState
					.removeFromStorage(productModel.read_token)
					// Swallow error: no action required
					.catch(() => Promise.resolve());
			}

			reportError(
				new Error('Could not find product to remove'),
				'warning',
			);

			return Promise.resolve();
		})
		.then(() => (
			ProductsModule.getById(productId)
				? ProductsModule.destroyModel(productId)
				: softDeleteProduct(productId)
		));
}
